import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AdditionalNotification, DisplayStatus, PhoneNumber, Recipient } from '../../interface/docusign-envelope-recipients-interface';
import { DocusignService } from '@lmc-app/core/services/docusign.service';
import { Subscription } from 'rxjs';
import { ListenerService } from '@lmc-app/core/services/listener.service';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { CorrectRecipientDeliveryComponent } from './correct-recipient-delivery/correct-recipient-delivery.component';
import { RecipientListSettings } from '../../constants/docusign-constants';
import { User } from '@lmc-app/shared/interface/user.interface';

@Component({
  selector: 'app-agreements-recipient-status',
  templateUrl: './agreements-recipient-status.component.html',
  styleUrls: ['./agreements-recipient-status.component.scss']
})
export class AgreementsRecipientStatusComponent implements OnInit, OnDestroy {
  @Input() envelopeId!: string;

  recipients: Recipient[] = [];
  currentRoutingOrder = 1;
  loading = true;
  error: string | null = null;
  private subs = new Subscription();

  constructor(
    private _docusignService: DocusignService,
    private _listenerService: ListenerService,
    private _bottomSheet: MatBottomSheet
  ) {}
 
  ngOnInit(): void {
    if (!this.envelopeId) return;
    this.loading = true;
    this.error = null;
    this.getRecipientStatuses(this.envelopeId);

    this.subs.add(
      this._listenerService.on<boolean>('recipientStatus').subscribe((refresh) => {        
        this.loading = true;
        if (refresh) {
          this.getRecipientStatuses(this.envelopeId);
        }
      })
    );
  }

  getRecipientDisplayStatus(r: Recipient): DisplayStatus {
    if (r.status === 'completed') return { label: 'Completed', tone: 'done' };
    if (r.status === 'declined') return { label: 'Declined', tone: 'declined' };
    if (this.isDeliveryFailed(r)) return { label: 'Delivery failed', tone: 'failed' };
    if (r.status === 'created') return { label: 'Not yet reached', tone: 'pending' };
    if (r.recipientType === 'certifieddelivery') return { label: 'Needs to View', tone: 'view' };
    return { label: 'Needs to Sign', tone: 'sign' };
  }

  // Mark as delivery failed if the email is incorrect
  isDeliveryFailed(r: Recipient): boolean {
    if (r.status === 'autoresponded') return true;
    if (this.getPrimaryChannelLabel(r)) return false;
    return !AgreementsRecipientStatusComponent.EMAIL_PATTERN.test((r.email || '').trim());
  }

  // True when SMS/WhatsApp IS the primary delivery channel (not layered on top of email)
  isPrimaryChannel(r: Recipient, method: 'sms' | 'whatsapp'): boolean {
    return (r.deliveryMethod || '').toLowerCase() === method;
  }

  getPrimaryChannelLabel(r: Recipient): 'SMS' | 'WhatsApp' | null {
    const m = (r.deliveryMethod || '').toLowerCase();
    if (m === 'sms') return 'SMS';
    if (m === 'whatsapp') return 'WhatsApp';
    return null;
  }

  formatPhone(p?: PhoneNumber): string {
    if (!p) return '';
    return `+${p.countryCode} ${p.number}`;
  }

  getSecondaryChannels(r: Recipient): AdditionalNotification[] {
    const raw = r.additionalNotifications as unknown;
    if (!raw) return [];
    return Array.isArray(raw) ? raw : [raw as AdditionalNotification];
  }

  getSentTime(r: Recipient): { label: string; value: string } | null {
    if (r.status === 'created') return null;
    return { label: 'Sent', value: r.sentDateTime };
  }

  getResolvedTime(r: Recipient): { label: string; value: string } | null {
    if (r.status === 'completed' && r.signedDateTime) {
      return { label: 'Signed', value: r.signedDateTime };
    }
    if (r.status === 'declined' && r.declinedDateTime) {
      return { label: 'Declined', value: r.declinedDateTime };
    }
    return null;
  }

  formatDateTime(iso?: string): string | null {
    if (!iso) return null;
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  trackByEmail(_index: number, r: Recipient): string {
    return r.email;
  }

  getInitials(roleName?: string): string {
    return (roleName || '').slice(0, 2).toUpperCase();
  }

  getDisplayName(name?: string, maxLength = 50): string {
    const trimmed = (name || '').trim();
    return trimmed.length > maxLength ? `${trimmed.slice(0, maxLength)}…` : trimmed;
  }

  getAvatarColor(roleName?: string): string {
    const initials = this.getInitials(roleName);
    return (
      RecipientListSettings.AVATAR_COLORS[initials] ||
      RecipientListSettings.DEFAULT_AVATAR_COLOR
    );
  }

  getRecipientStatuses(envelopeId:string){
    this._docusignService.getEnvelopeRecipientStatus(envelopeId).subscribe({
      next: (res) => {
        console.log(res,"_______________________________________________")
        this.currentRoutingOrder = res.currentRoutingOrder;
        this.recipients = [...res.signers, ...res.certifiedDeliveries].sort(
          (a, b) => a.routingOrder - b.routingOrder
        );
        this.loading = false;
      },
      error: (err) => {
        console.error(err);
        this.error = 'Could not load envelope status.';
        this.loading = false;
      },
    });
  }

  // ---------- Edit mode ----------
  editingRecipientId: string | null = null;
  private static readonly EDITABLE_ROLES = ['client', 'co-applicant'];
  private static readonly EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  canEdit(r: Recipient): boolean {
    // No editing once the recipient has already acted
    const tone = this.getRecipientDisplayStatus(r).tone;
    if (tone === 'done' || tone === 'declined') return false;
 
    // Only Client and Co-Applicant roles are editable
    const role = (r.roleName || '').trim().toLowerCase();
    return AgreementsRecipientStatusComponent.EDITABLE_ROLES.includes(role);
  }
 
  correctionData: any;
  onRowClick(r: Recipient): void {
    console.log('row clicked:', r.recipientId, 'canEdit:', this.canEdit(r));
    if (!this.canEdit(r)) return;
 
    if (this.editingRecipientId === r.recipientId) {
      // Clicking the already-open row again does nothing (form has its own Cancel button)
      return;
    }
    this.correctionData = r;
    // this.startEdit(r);
    this.openBottomSheetEdit()
  }

  openBottomSheetEdit(): void {
    const bottomSheetRef = this._bottomSheet.open(CorrectRecipientDeliveryComponent, {
      panelClass: 'right-side-bottom-sheet',
        data:  { envelopeId: this.envelopeId, recipient:  this.correctionData, appData: this.appData, currentUser: this.currentUser }
    });
    bottomSheetRef.afterDismissed().subscribe((result) => {
      this.loading = true;
      if(result){
        this.getRecipientStatuses(this.envelopeId);
      } else{
        this.loading = false;
      }
    })
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ---------- Zoho sync ----------
  // Passed to the edit sheet so recipient corrections are also saved to Zoho
  @Input() appData: any;
  @Input() currentUser: User;
}
